import React, {FC, useCallback, useEffect, useState} from 'react'
import {Box, FormHelperText, IconButton, InputLabel, MenuItem, Select, Typography} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {useHistory, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {observer} from 'mobx-react-lite'
import {ImageBlotProps} from '@components/TextEditor/Blots/ImageBlot/ImageBlotProps'
import {InputStringStoriesWithValidation} from '@components/UI/InputStringStoriesWithValidation/InputStringStoriesWithValidation'
import {useStore} from '@stores/rootStoreContext'
import SimpleModal from '../../UI/SimpleModal/SimpleModal'
import {warnColor} from '../../../@theme/vars'
import StoryIncutBlockModal from '../StoryIncutBlockModal/StoryIncutBlockModal'
import {FigureVersion, IncutType, SaveStoriesIncutBlockInput, StoriesIncutBlock} from '../../../@graphql/types'
import {useSaveStoriesIncutBlockByIdMutation, useStoriesIncutBlockByIdQuery} from './StoryIncutEdit.generated'
import {STORY_INCUT_EDIT_CONSTS} from './StoryIncutEdit.consts'
import {
  HeaderWithBorder,
  InfoContainersFlex,
  MainContainer,
  StyledDeleteButton,
  StyledSaveButton,
  Title,
  TitleLength,
  TitleWithLengthContainer,
  UrlFormControl
} from './StoryIncutEdit.styles'

const StoryIncutEdit: FC = () => {
  const {cropperStore, storiesIncutStore} = useStore()
  const {cropper, setCropper} = cropperStore
  const {addingBlocksCache, storiesIncutBlocks, setStoriesIncutBlocks} = storiesIncutStore
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [figureVersion, setFigureVersion] = useState<FigureVersion | null>(null)
  const [incutType, setIncutType] = useState<IncutType | ''>('')
  const [titleErrorText, setTitleErrorText] = useState('')
  const [disableFormState, setDisableFormState] = useState(false)
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [blockImage, setBlockImage] = useState<ImageBlotProps>(STORY_INCUT_EDIT_CONSTS.defaultImage)
  const history = useHistory()
  const {storyId, uid} = useParams<{storyId: string; uid: string}>()

  const {data, loading} = useStoriesIncutBlockByIdQuery({
    variables: {Id: uid},
    skip: !uid,
    onCompleted: data => {
      const block = data?.storiesIncutBlockById
      if (block) {
        setTitle(block.title || '')
        setIncutType(block.incutType || '')
        setUrl(block.url || '')
        setFigureVersion(block.figureVersion || null)
        setBlockImage({
          id: `storyIncutEdit-${block.figureVersion?.figure?.id || ''}`,
          src: block.figureVersion?.primaryProcessedImage?.url || '/noPhoto.jpg',
          cropperPreviewUrl: block.figureVersion?.figure?.cropperPreview?.url || '',
          crop: {
            width: block.figureVersion?.crop?.width || 0,
            height: block.figureVersion?.crop?.height || 0,
            top: block.figureVersion?.crop?.top || 0,
            left: block.figureVersion?.crop?.left || 0
          },
          figureId: block.figureVersion?.figure?.id || '',
          figureUid: block.figureVersion?.figure?.uid || '',
          figureVersionId: block.figureVersion?.id || '',
          isFullWidth: true,
          isannouncingphoto: false,
          isoverviewblock: false,
          hasWatermark: false,
          hasDiagonalWatermark: false
        })
      }
    }
  })

  const [saveStoriesIncutBlock, {loading: saving}] = useSaveStoriesIncutBlockByIdMutation()

  const setNewFigureVersion = useCallback(
    (value: FigureVersion | undefined) => {
      if (value) {
        const newFigureVersion: FigureVersion = {
          crop: {
            width: cropper.width || value.crop?.width || 0,
            height: cropper.height || value.crop?.height || 0,
            top: cropper.top || value.crop?.top || 0,
            left: cropper.left || value.crop?.left || 0
          },
          figure: value.figure,
          primaryProcessedImage: value.primaryProcessedImage,
          uid: value.uid,
          id: value.id
        }
        setFigureVersion(newFigureVersion)
        setBlockImage({
          id: `figure-${value.figure?.id || ''}`,
          src: value.figure.latestVersion.primaryProcessedImage?.url || '/noPhoto.jpg',
          cropperPreviewUrl: value.figure?.cropperPreview?.url || '',
          crop: {
            width: cropper.width || value.crop?.width || 0,
            height: cropper.height || value.crop?.height || 0,
            top: cropper.top || value.crop?.top || 0,
            left: cropper.left || value.crop?.left || 0
          },
          figureId: value.figure?.id || '',
          figureUid: value.figure?.uid || '',
          figureVersionId: value.id || '',
          isFullWidth: true,
          isannouncingphoto: false,
          isoverviewblock: false,
          hasWatermark: false,
          hasDiagonalWatermark: false
        })
      } else {
        setFigureVersion(null)
        setBlockImage(STORY_INCUT_EDIT_CONSTS.defaultImage)
      }
    },
    [cropper, setBlockImage] // Добавляем setBlockImage в зависимости
  )

  useEffect(() => {
    setDisableFormState(!url || !title || !incutType)
  }, [url, title, incutType])

  const handleUpdateBlock = useCallback(async () => {
    try {
      const input: SaveStoriesIncutBlockInput = {
        id: uid,
        title,
        incutType: incutType as IncutType,
        figureVersionId: figureVersion?.id || undefined,
        url
      }
      const res = await saveStoriesIncutBlock({
        variables: {data: input},
        update(cache, {data}) {
          if (data?.saveStoriesIncutBlock?.block) {
            cache.modify({
              id: cache.identify({__typename: 'StoriesIncutBlock', uid: data.saveStoriesIncutBlock.block.uid}),
              fields: {
                figureVersion() {
                  return figureVersion ? {...figureVersion, __typename: 'FigureVersion'} : null
                },
                title() {
                  return title
                },
                incutType() {
                  return incutType
                },
                url() {
                  return url
                }
              }
            })
          }
        }
      })
      if (!res.errors?.length && res.data?.saveStoriesIncutBlock?.block) {
        const cachedBlock = addingBlocksCache.get(uid)
        const storeBlock = storiesIncutBlocks.find(b => b.uid === uid)
        const currentPosition = cachedBlock?.position ?? storeBlock?.position ?? 0

        console.log('Block position check:', {
          uid,
          cachedPosition: cachedBlock?.position,
          storePosition: storeBlock?.position,
          finalPosition: currentPosition
        })

        const updatedBlock = {
          ...res.data.saveStoriesIncutBlock.block,
          title,
          incutType: incutType as IncutType,
          url,
          figureVersion: figureVersion ? {...figureVersion} : null,
          position: currentPosition
        } as StoriesIncutBlock

        if (!storeBlock) {
          addingBlocksCache.set(uid, updatedBlock)
        }

        setStoriesIncutBlocks(storiesIncutBlocks.map(block => (block.uid === uid ? updatedBlock : block)))

        history.push(`/story/editor/${storyId}?tab=incuts`)
        toast.success(STORY_INCUT_EDIT_CONSTS.successTopNewsMsg, {
          autoClose: 1000,
          hideProgressBar: true,
          containerId: 'success'
        })
      }
    } catch (e) {
      console.error(e)
      toast.error('Ошибка при сохранении врезки', {containerId: 'error'})
    }
  }, [
    saveStoriesIncutBlock,
    addingBlocksCache,
    uid,
    title,
    incutType,
    figureVersion,
    url,
    history,
    storyId,
    storiesIncutBlocks,
    setStoriesIncutBlocks
  ])

  const goBack = useCallback(() => {
    history.push(`/story/editor/${storyId}?tab=incuts`)
  }, [history, storyId])

  const openModalImage = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  return (
    <>
      <HeaderWithBorder>
        <IconButton onClick={goBack}>
          <ArrowBackIcon />
        </IconButton>
        <Title>{STORY_INCUT_EDIT_CONSTS.titleMsg}</Title>
      </HeaderWithBorder>
      <MainContainer>
        <div id='story-incut-edit-image' onClick={openModalImage}>
          {blockImage && (
            <figure {...blockImage}>
              {blockImage.src && (
                <img
                  className='change-item'
                  draggable='false'
                  src='/changeItem.jpg'
                  onClick={openModalImage}
                  alt='Change'
                />
              )}
              <img src={blockImage.src || '/noPhoto.jpg'} draggable='false' alt='Incut' />
            </figure>
          )}
        </div>
        <InfoContainersFlex>
          <UrlFormControl>
            <InputLabel>{STORY_INCUT_EDIT_CONSTS.typeMsg}</InputLabel>
            <Select
              label={STORY_INCUT_EDIT_CONSTS.typeMsg}
              onChange={e => setIncutType(e.target.value as IncutType)}
              value={incutType}
            >
              {STORY_INCUT_EDIT_CONSTS.typeIncut.map(({value, name}) => (
                <MenuItem key={value} value={value}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </UrlFormControl>
          <TitleWithLengthContainer>
            <InputStringStoriesWithValidation
              id='story-incut-edit-title'
              title={STORY_INCUT_EDIT_CONSTS.titleNameMsg}
              defaultValue={title}
              setValue={setTitle}
              setErrorText={setTitleErrorText}
              showTitleLength={false}
              maxLength={STORY_INCUT_EDIT_CONSTS.maxTitleLength}
              width='85%'
              required={true}
            />
            <TitleLength style={{color: titleErrorText ? warnColor : 'inherit'}}>
              {`${title?.length || 0} / ${STORY_INCUT_EDIT_CONSTS.maxTitleLength}`}
            </TitleLength>
          </TitleWithLengthContainer>
          <StyledSaveButton
            color='primary'
            disabled={disableFormState || saving}
            variant='contained'
            onClick={handleUpdateBlock}
          >
            {STORY_INCUT_EDIT_CONSTS.saveMsg}
          </StyledSaveButton>
        </InfoContainersFlex>
      </MainContainer>
      <SimpleModal
        open={isOpenConfirmDialog}
        mode='alert'
        variant='error'
        title={STORY_INCUT_EDIT_CONSTS.warningMsg}
        okLabel={STORY_INCUT_EDIT_CONSTS.closeConfirmBtn}
        yesLabel={STORY_INCUT_EDIT_CONSTS.closeConfirmBtn}
        onOk={() => setIsOpenConfirmDialog(false)}
        onYes={() => setIsOpenConfirmDialog(false)}
        onClose={() => setIsOpenConfirmDialog(false)}
        onAbort={() => setIsOpenConfirmDialog(false)}
        onNo={() => setIsOpenConfirmDialog(false)}
      >
        <Box textAlign='center' style={{marginBottom: '10px'}}>
          <Typography variant='h2'>{STORY_INCUT_EDIT_CONSTS.closeConfirmationText}</Typography>
        </Box>
      </SimpleModal>
      <StoryIncutBlockModal
        figureVersion={figureVersion}
        open={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        setFigureVersion={setNewFigureVersion}
        incutUid={uid}
      />
    </>
  )
}

export default observer(StoryIncutEdit)
